import {
    isEmpty as _isEmpty,
    sortBy as _sortBy,
    countBy as _countBy,
    partition as _partition,
    forEach as _forEach,
    uniqBy as _uniqBy,
} from '@lodash';
import { Commit, Dispatch } from 'vuex';
import { Subscription } from 'rxjs';
import Match from '@core/models/offer/Match';
import StoreUtil from '@store/StoreUtil';
import { getEventsBySport } from '@store/modules/ui/sportOffer/utils';
import { TournamentItem, SportsItem } from '@store/modules/ui/sportOffer/filter/types';
import Market from '@models/struct/Market';
import BookieDate from '@utils/date/BookieDate';
import DateUtils from '@utils/date/DateUtils';

import config from '@config';
import { IState } from '@app/store';
import OfferService from '@core/services/offer/OfferService';
import { getTimeSpanForTimeFilter } from '@shared/timeFilter/helpers';
import { TimeFilter } from '@shared/timeFilter/enums';

type Alphanum = number | string;

export interface IFilterState {
    sportId: string | undefined;
    tournaments: TournamentItem[];
    areTournamentsExpanded: boolean;
    market: Market | undefined;
    odd: Alphanum;
    oddTolerance: number | undefined;
}

const SET_SPORT_ID = 'SET_SPORT_ID';
const SET_ODD = 'SET_ODD';
const SET_ODD_TOLERANCE = 'SET_ODD_TOLERANCE';
const TOGGLE_SELECTED_TOURNAMENT = 'TOGGLE_SELECTED_TOURNAMENT';
const TOGGLE_TOURNAMENTS_ARE_EXPANDED = 'TOGGLE_TOURNAMENTS_ARE_EXPANDED';
const CLEAR_SELECTED_TOURNAMENTS = 'CLEAR_SELECTED_TOURNAMENTS';
const RESET = 'RESET';
const SET_MARKET = 'SET_MARKET';

const INIT_ODD = '';
const INIT_ODD_TOLERANCE = undefined;
const INIT_SPORT_ID = undefined;
const INIT_TOURNAMENTS: TournamentItem[] = [];
const INIT_ARE_TOURNAMENTS_EXPANDED = false;
const INIT_MARKET = undefined;

const subscriptions: Record<number, Subscription> = {};

function defaultState(): IFilterState {
    return {
        sportId: INIT_SPORT_ID,
        tournaments: INIT_TOURNAMENTS,
        areTournamentsExpanded: INIT_ARE_TOURNAMENTS_EXPANDED,
        market: INIT_MARKET,
        odd: INIT_ODD,
        oddTolerance: INIT_ODD_TOLERANCE,
    };
}

export default {
    namespaced: true,

    state: defaultState(),

    mutations: {
        [SET_SPORT_ID]: StoreUtil.createSimpleMutator('sportId'),
        [TOGGLE_SELECTED_TOURNAMENT]: StoreUtil.createToggleListElementMutatorBy('tournaments', 'id'),
        [TOGGLE_TOURNAMENTS_ARE_EXPANDED]: StoreUtil.createToggleMutator('areTournamentsExpanded'),
        [CLEAR_SELECTED_TOURNAMENTS]: (state: IFilterState) => {
            state.tournaments = INIT_TOURNAMENTS;
        },
        [SET_MARKET]: StoreUtil.createSimpleMutator('market'),
        [RESET]: (state: IFilterState) => {
            const defaults = defaultState();
            Object.assign(state, defaults);
        },
        [SET_ODD]: StoreUtil.createSimpleMutator('odd'),
        [SET_ODD_TOLERANCE]: StoreUtil.createSimpleMutator('oddTolerance'),
    },

    getters: {
        /**
         * Shows only sports which have number of events > 0 or previously selected sportOffer
         */
        sportsWithEvents(state: IFilterState, getters: any, rootState: IState, rootGetters: any) {
            return getEventsBySport(
                rootGetters['data/sportOffer/events'],
                rootGetters['data/sportOffer/sports'],
                '',
                []
            );
        },
        selectedSport(state: IFilterState, getters: any, rootState: IState, rootGetters: any)
            : SportsItem | undefined {
            if (!getters.sportId) {
                return undefined;
            }

            const sportsWithEvents = getters.sportsWithEvents
                .filter((sport: SportsItem) => sport.id === getters.sportId);

            // if selected sport does not exist anymore preserve it
            if (_isEmpty(sportsWithEvents)) {
                const sport = rootGetters['data/sportOffer/sports'][getters.sportId];

                return {
                    id: sport.id,
                    name: sport.name,
                    eventCount: 0,
                };
            }

            return sportsWithEvents[0];
        },
        sportsTournaments(state: IFilterState, getters: any, rootState: IState, rootGetters: any) {
            const eventsCountByTournament = _countBy(
                rootGetters['data/sportOffer/events'],
                (event: Match) => event.tournamentId
            );
            let existingTournaments = (Object.values(rootGetters['data/sportOffer/events']) as Match[])
                .filter((event: Match) => event.sportId === getters.sportId)
                .map((event: Match) => {
                    return {
                        id: event.tournamentId,
                        name: event.tournamentName,
                        isSelected: getters._tournamentIds.has(event.tournamentId),
                        order: event.tournamentOrder,
                        eventCount: eventsCountByTournament[event.tournamentId]
                    } as TournamentItem;
                });

            existingTournaments = _uniqBy(existingTournaments, (tournament: TournamentItem) => tournament.id);
            const existingTournamentIds = new Set(existingTournaments.map((c) => c.id));
            const nonExistingSelectedTournaments = (Object.values(getters.tournaments) as TournamentItem[])
                .filter((c: TournamentItem) => !existingTournamentIds.has(c.id));

            nonExistingSelectedTournaments.forEach(
                (selectedTournament: TournamentItem) => selectedTournament.eventCount = 0);

            return _sortBy(
                [
                    ...existingTournaments,
                    ...nonExistingSelectedTournaments
                ],
                ['order', 'id']
            );
        },
        filteredEvents(state: IFilterState, getters: any): Match[] {
            const filters: Function[] = [];

            // market is selected but does not exist in tournament's events anymore
            if (getters.market && !getters._data.markets[getters.market.id]) {
                return [];
            }

            if (getters.market) {
                filters.push((event: Match) => getters._data.markets[getters.market.id].eventIds.has(event.id));
            }

            // todo to highlight, design still in progress
            if (getters.odd) {
                const odd = parseInt(getters.odd, 10);
                let minOdd = odd;
                let maxOdd = odd;

                if (getters.oddTolerance) {
                    minOdd = odd - getters.oddTolerance;
                    maxOdd = odd + getters.oddTolerance;
                }

                filters.push((event: Match) => event.hasOddsInRange(minOdd, maxOdd));
            }

            return getters._eventsForSelectedTournaments
                .filter((event: Match) => filters.every((func: Function) => func(event)));
        },
        sportId(state: IFilterState): string | undefined {
            return state.sportId;
        },
        tournaments(state: IFilterState): TournamentItem[] {
            return state.tournaments;
        },
        areTournamentsExpanded(state: IFilterState): boolean {
            return state.areTournamentsExpanded;
        },
        markets(state: IFilterState, getters: any) {
            const data: IData = getters._data;

            const markets = Object.values(data.markets)
                .filter((dataMarket) => dataMarket.tournamentIds.size === data.tournamentIds.size)
                .map((marketEvents) => marketEvents.market);

            // case when somebody selects market and than after a while all events of that market
            // finish.  We still wanna have this market as selected
            if (getters.market && !(markets.find((market) => market.id === getters.market.id))) {
                markets.push(getters.market);
            }

            return markets;
        },
        market(state: IFilterState, getters: any): Market | undefined {
            return state.market;
        },
        odd(state: IFilterState): Alphanum {
            return state.odd;
        },
        oddTolerance(state: IFilterState): number | undefined {
            return state.oddTolerance;
        },
        oddTolerances(state: IFilterState, getters: any): number[] {
            const tolerances = config.app.sportOffer.filter.oddTolerance;
            const defaultTolerance = tolerances[0].values;

            if (!getters.odd) {
                return defaultTolerance;
            }

            for (const tolerance of tolerances) {
                const isInLowerBorder = tolerance.from <= getters.odd;
                const isInUpperBorder = tolerance.to === undefined || getters.odd < tolerance.to;
                if (isInLowerBorder && isInUpperBorder) {
                    return tolerance.values;
                }
            }

            return defaultTolerance;
        },
        _data(state: IFilterState, getters: any): IData {
            // we need this helper structure to find available markets
            // for selected tournaments
            const map: IData = {
                markets: {},
                tournamentIds: new Set<number>()
            };

            getters._eventsForSelectedTournaments.forEach((event: Match) => {
                event.getMarkets().forEach((market) => {
                    let dataMarket;

                    if (map.markets[market.id]) {
                        dataMarket = map.markets[market.id];
                    } else {
                        dataMarket = {
                            market,
                            tournamentIds: new Set<number>(),
                            eventIds: new Set<number>(),
                        };
                        map.markets[market.id] = dataMarket;
                    }

                    dataMarket.tournamentIds.add(event.tournamentId);
                    dataMarket.eventIds.add(event.id);
                });
                map.tournamentIds.add(event.tournamentId);
            });

            return map;

        },
        _eventsForSelectedTournaments(state: IFilterState, getters: any, rootState: IState, rootGetters: any): Match[] {
            const filters = [
                (event: Match) => getters._tournamentIds.has(event.tournamentId),
            ];

            return rootGetters['data/sportOffer/events']
                .filter((event: Match) => filters.every((func: Function) => func(event)));
        },
        _tournamentIds(state: IFilterState, getters: any): Set<number> {
            return new Set(getters.tournaments.map((c: TournamentItem) => c.id));
        },
    },

    actions: {
        toggleSelectedTournament(
            { commit, dispatch, getters, rootGetters }:
                { commit: Commit; dispatch: Dispatch; getters: any; rootGetters: any },
            tournament: TournamentItem
        ) {

            commit(TOGGLE_SELECTED_TOURNAMENT, tournament);

            if (getters._tournamentIds.has(tournament.id)) {
                const timeSpan = getTimeSpanForTimeFilter(TimeFilter.all);

                // when we select tournament we wanna immediately subscribe to all tournament's events
                // because we wanna new changes with full odds (by default changes comes only with final market odds),
                // otherwise, subset of odds would overwrite our newly fetched events
                subscribeToEvents(getters._eventsForSelectedTournaments
                    .filter((event: Match) => event.tournamentId === tournament.id));

                // after we subscribe to all events we can safely fetch tournament's events
                dispatch(
                    'data/sportOffer/fetchTournament',
                    {
                        timeSpan,
                        tournamentId: tournament.id,
                        preselected: false
                    },
                    { root: true }
                );
            } else {
                unsubscribeFromEvents(rootGetters['data/sportOffer/events']
                    .filter((event: Match) => event.tournamentId === tournament.id));
            }
        },
        removeSport({ commit }: { commit: Commit }): void {
            commit(SET_SPORT_ID, undefined);
            commit(CLEAR_SELECTED_TOURNAMENTS);
            commit(SET_ODD, INIT_ODD);
            commit(SET_ODD_TOLERANCE, INIT_ODD_TOLERANCE);
            clearAllSubscriptions();
        },
        reset({ commit }: { commit: Commit }): void {
            commit(RESET);
            clearAllSubscriptions();
        },
        subscribeToEventChanges({ getters }: { getters: any }) {
            OfferService.getInstance().subscribeToAllEventChanges((events) => {
                if (_isEmpty(events)) {
                    return;
                }
                const [finishedEvents, ongoingEvents] = _partition(events, (e) => e.isFinished());

                subscribeToEvents(ongoingEvents
                    .filter((event: Match) =>
                        getters._tournamentIds.has(event.tournamentId) && !subscriptions[event.id]));
                unsubscribeFromEvents(finishedEvents);
            });
        },
        setOdd({ commit, dispatch }: { commit: Commit; dispatch: Dispatch }, value: Alphanum): void {
            dispatch('setOddTolerance', INIT_ODD_TOLERANCE);

            if (value === undefined) {
                commit(SET_ODD, INIT_ODD);
            } else {
                commit(SET_ODD, value);
            }
        },
        resetOdds({ commit, dispatch }: { commit: Commit; dispatch: Dispatch }): void {
            dispatch('setOdd', INIT_ODD);
            dispatch('setOddTolerance', INIT_ODD_TOLERANCE);
        },
        setOddTolerance: StoreUtil.createSimpleMutatorAction(SET_ODD_TOLERANCE),
        setMarket: StoreUtil.createSimpleMutatorAction(SET_MARKET),
        setSportId: StoreUtil.createSimpleMutatorAction(SET_SPORT_ID),
        toggleAreTournamentsExpanded: StoreUtil.createSimpleMutatorAction(TOGGLE_TOURNAMENTS_ARE_EXPANDED),
    },
};

interface IData {
    markets: Record<number, IDataMarket>;
    tournamentIds: Set<number>;
}

interface IDataMarket {
    market: Market;
    tournamentIds: Set<number>;
    eventIds: Set<number>;
}

function subscribeToEvents(events: Match[]) {
    // Callback doesn't do anything.  We only need subscription.  We only need the full odd list
    // Offer layer will handle updating the store for us.
    events.forEach((event: Match) => {
        subscriptions[event.id] = OfferService.getInstance().subscribeToEvent(event.id, () => {
        });
    });
}

function unsubscribeFromEvents(events: Match[]) {
    events.forEach((event: Match) => {
        if (subscriptions[event.id]) {
            subscriptions[event.id].unsubscribe();
        }
    });
}

function clearAllSubscriptions() {
    _forEach(subscriptions, (subscription) => subscription.unsubscribe());
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--12!./src/app/store/modules/ui/sportOffer/filter/filterIndex.ts